"use client"
import {
  POLICY_ICONS,
  POLICY_STATUS_LABELS,
  POLICY_STATUS_ORDER,
  type PoliciesByStatus,
  type Policy,
  type PolicyStatus,
} from "./policiesData"
import { useHudContext } from "../../shared/playbookHUDInfra"
import { POLICY_DESCRIPTIONS } from "./policyDescriptions"
import {
  POLICY_ITEM_APPEARANCE,
  type PolicyItemAppearance,
} from "./policies-panel/policyAppearance"

// Shared width of BOTH tooltip boxes — the full policy list and the
// single-policy card hanging below it — so the stacked pair stays
// edge-aligned instead of the list sizing to its content.
const POLICY_TOOLTIP_WIDTH = 280

// One horizontal inset shared by every row of both tooltip boxes (header,
// section headers, policy rows, and the single-policy card), so all their
// left edges line up and the padding reads as roomy as the metric tooltips.
const TOOLTIP_INSET_X = "px-5"

// Mini copy of the panel's policy box, sized for a tooltip row. Deliberately
// static (no motion/layoutId) so the panel's LayoutGroup can't adopt it and
// animate boxes between the panel and the tooltip.
const ROW_BOX_SIZE = 16
const ROW_ICON_SIZE = 10
const ROW_BOX_RADIUS = 4

// Tooltip renders with disableShadow; this filter follows the rendered alpha
// of the content itself so the box casts the same shadow as other tooltips.
const TOOLTIP_SHADOW_FILTER =
  "drop-shadow(1px 1px 3px rgba(0, 0, 0, 0.2)) drop-shadow(1px 1px 300px rgba(10, 10, 0, 0.1))"

const PolicyRowBox = ({
  policy,
  appearance,
  borderRadius,
  strokeWidth,
}: {
  policy: Policy
  appearance: PolicyItemAppearance
  borderRadius?: number
  strokeWidth?: number
}) => {
  const IconComponent = policy.icon ? POLICY_ICONS[policy.icon] : undefined
  return (
    <span
      className="flex items-center justify-center shrink-0"
      style={{
        width: ROW_BOX_SIZE,
        height: ROW_BOX_SIZE,
        borderRadius: borderRadius ?? ROW_BOX_RADIUS,
        borderWidth: strokeWidth ?? 1,
        borderStyle: appearance.borderStyle,
        borderColor: appearance.borderColor,
        backgroundColor: appearance.fillColor,
        color: appearance.iconColor,
      }}
    >
      {IconComponent && (
        <IconComponent size={ROW_ICON_SIZE} strokeWidth={2.2} />
      )}
    </span>
  )
}

const PolicyTooltipRow = ({
  policy,
  appearance,
  borderRadius,
  strokeWidth,
  opacity,
  highlighted,
}: {
  policy: Policy
  appearance: PolicyItemAppearance
  borderRadius?: number
  strokeWidth?: number
  opacity: number
  // The row for the policy box currently hovered in the panel — gets a subtle
  // background wash so the eye can find its label in the full list.
  highlighted: boolean
}) => (
  <div
    className={`flex items-center gap-2 py-[3px] ${TOOLTIP_INSET_X} text-[12px] ${highlighted ? "bg-black/[0.07]" : ""}`}
    style={{ opacity }}
  >
    <PolicyRowBox
      policy={policy}
      appearance={appearance}
      borderRadius={borderRadius}
      strokeWidth={strokeWidth}
    />
    <span className={`min-w-0 leading-tight ${highlighted ? "font-semibold" : ""}`}>
      {policy.label}
    </span>
  </div>
)

const SectionHeader = ({ children }: { children: string }) => (
  <div className={`${TOOLTIP_INSET_X} opacity-60 py-2 text-[10px] font-mono uppercase tracking-wide leading-tight`}>
    {children}
  </div>
)

// Tooltip body for PoliciesPanel. Hovering ANYWHERE on the panel (labels or
// boxes) shows every policy grouped by status, each row led by a mini copy of
// its panel box (same per-status appearance, same icon). The tooltip is
// placed left-end, so the list's bottom edge stays aligned with the panel's
// bottom edge. Hovering an INDIVIDUAL policy box keeps the list up and adds a
// compact single-policy card — name plus a one-line layperson description —
// as a second box hanging below the list (top: 100%), i.e. top-aligned with
// the panel's bottom edge.
export const PoliciesPanelTooltipContent = ({
  grouped,
  hoveredPolicyId,
  statusAppearance,
  borderRadius,
  strokeWidth,
}: {
  grouped: PoliciesByStatus
  hoveredPolicyId: string | null
  // Resolved per-status appearance actually rendered by the panel boxes (see
  // resolvePolicyStatusAppearances). Defaults to the base appearance map.
  statusAppearance?: Record<PolicyStatus, PolicyItemAppearance>
  borderRadius?: number
  strokeWidth?: number
}) => {
  const { year } = useHudContext()
  const appearances = statusAppearance ?? POLICY_ITEM_APPEARANCE

  const hoveredPolicy = hoveredPolicyId
    ? POLICY_STATUS_ORDER.flatMap((status) => grouped[status]).find((p) => p.id === hoveredPolicyId)
    : undefined

  return (
    <div className="relative font-sans" style={{ filter: TOOLTIP_SHADOW_FILTER }}>
      <div
        className="bg-[#fffff8] rounded-sm text-[11px] py-3 leading-tight text-black"
        style={{ width: POLICY_TOOLTIP_WIDTH }}
      >
        <div className={`flex justify-between items-center font-mono ${TOOLTIP_INSET_X} border-b border-black/10 pb-1`}>
          <div className="pb-1.5 text-[12px] font-semibold tracking-wide uppercase">
            Policies
          </div>
          <div className="text-[12px] font-semibold tracking-wide opacity-100">
            {year}
          </div>
        </div>
        <div className="mb-1" />
        {POLICY_STATUS_ORDER.map((status) => {
          const policiesInSection = grouped[status]
          const sectionOpacity = status === "off_the_table" ? 0.7 : 1
          return (
            <div
              key={status}
              className="mb-1 last:mb-0"
            >
              <SectionHeader>{POLICY_STATUS_LABELS[status]}</SectionHeader>
              {policiesInSection.map((policy) => (
                <PolicyTooltipRow
                  key={policy.id}
                  policy={policy}
                  appearance={appearances[status]}
                  borderRadius={borderRadius}
                  strokeWidth={strokeWidth}
                  opacity={sectionOpacity}
                  highlighted={policy.id === hoveredPolicyId}
                />
              ))}
            </div>
          )
        })}
      </div>
      {/* Single-policy card, shown while an individual box is hovered. It
          hangs off the list's bottom edge (which placement left-end pins to
          the panel's bottom edge), so its top aligns with the bottom of the
          policy section — with a 6px gap so it reads as a separate tooltip.
          right-0 keeps it against the panel side even when narrower than
          the list. */}
      {hoveredPolicy && (
        <div
          className="absolute right-0 top-full mt-1.5 bg-[#fffff8] rounded-sm text-black"
          style={{ width: POLICY_TOOLTIP_WIDTH }}
        >
          <div className={`${TOOLTIP_INSET_X} py-4`}>
            <div className="font-bold text-[13px] mb-1">{hoveredPolicy.label}</div>
            <div className="text-[12px] leading-relaxed whitespace-normal">
              {POLICY_DESCRIPTIONS[hoveredPolicy.id] ?? hoveredPolicy.description}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PoliciesPanelTooltipContent
